/**
 * PlanRequestsManagement — Admin view of trip-plan booking enquiries.
 *
 * Route: /admin/plan-requests
 *
 * Users who finalize a custom itinerary can "Request This Trip", which creates
 * a PlanRequest. This screen lets the team see those enquiries, open the full
 * itinerary + requester details, and move each one through its lifecycle
 * (pending → contacted → confirmed → closed).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Groups,
  Event,
  Person,
  Email,
  Phone,
  OpenInNew,
  BookOnline as BookIcon,
} from '@mui/icons-material';
import adminService from '../../services/adminService';

const STATUS_OPTIONS = ['pending', 'contacted', 'confirmed', 'closed'];

const STATUS_COLORS = {
  pending: 'warning',
  contacted: 'info',
  confirmed: 'success',
  closed: 'default',
};

const formatCurrency = (value, currency = 'USD') => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const PlanRequestsManagement = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [bookOpen, setBookOpen] = useState(false);
  const [bookForm, setBookForm] = useState({ travel_date: '', num_travelers: '', total_price: '' });
  const [booking, setBooking] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const filters = { page: page + 1, per_page: rowsPerPage };
      if (statusFilter !== 'all') filters.status = statusFilter;
      const res = await adminService.getPlanRequests(filters);
      const list = res?.planRequests ?? [];
      setRequests(list);
      setTotalCount(res?.pagination?.total_count ?? res?.pagination?.total ?? list.length);
    } catch (err) {
      setRequests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleView = (req) => {
    setSelected(req);
    setOpenDialog(true);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selected || !newStatus || newStatus === selected.status) return;
    try {
      setUpdating(true);
      const res = await adminService.updatePlanRequest(selected.id, newStatus);
      const updated = res?.planRequest ?? { ...selected, status: newStatus };
      setSelected(updated);
      setRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, status: updated.status } : r)));
      setSnackbar({ open: true, message: `Marked as ${newStatus}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Failed to update status', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const tomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const openBook = () => {
    if (!selected) return;
    setBookForm({
      travel_date: selected.preferred_date && selected.preferred_date >= tomorrow() ? selected.preferred_date : '',
      num_travelers: selected.num_travelers || 1,
      total_price: selected.plan?.calculated_total ?? '',
    });
    setBookOpen(true);
  };

  const handleBook = async () => {
    if (!selected) return;
    if (!bookForm.travel_date || bookForm.travel_date < tomorrow()) {
      setSnackbar({ open: true, message: 'Choose a future travel date.', severity: 'error' });
      return;
    }
    try {
      setBooking(true);
      const res = await adminService.bookPlanRequest(selected.id, {
        travel_date: bookForm.travel_date,
        num_travelers: parseInt(bookForm.num_travelers, 10) || 1,
        total_price: bookForm.total_price === '' ? undefined : parseFloat(bookForm.total_price),
      });
      const ref = res?.booking?.reference;
      setSelected((s) => (s ? { ...s, status: 'confirmed' } : s));
      setRequests((prev) => prev.map((r) => (r.id === selected.id ? { ...r, status: 'confirmed' } : r)));
      setBookOpen(false);
      setOpenDialog(false);
      setSnackbar({ open: true, message: `Booked${ref ? ` (${ref})` : ''} and customer notified`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Booking failed', severity: 'error' });
    } finally {
      setBooking(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Trip Requests
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Booking enquiries from customers who finalized a custom itinerary.
      </Typography>

      {/* Status filter */}
      <ToggleButtonGroup
        value={statusFilter}
        exclusive
        onChange={(e, val) => { if (val) { setStatusFilter(val); setPage(0); } }}
        size="small"
        sx={{ mb: 3, flexWrap: 'wrap', '& .MuiToggleButton-root': { borderRadius: 9999, px: 2, textTransform: 'capitalize', mr: 1, mb: 1, border: `1px solid ${theme.palette.grey[300]}` } }}
      >
        <ToggleButton value="all">All</ToggleButton>
        {STATUS_OPTIONS.map((s) => (
          <ToggleButton key={s} value={s}>{s}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Groups sx={{ fontSize: 56, color: theme.palette.text.disabled, mb: 1 }} />
            <Typography color="text.secondary">No trip requests yet.</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                <TableCell>Plan</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell align="center">Travelers</TableCell>
                <TableCell>Preferred Date</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{req.plan?.title || 'Untitled plan'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {req.requester?.name || '—'}
                      {req.is_guest && (
                        <Chip label="Guest" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">{req.num_travelers ?? '—'}</TableCell>
                  <TableCell>{formatDate(req.preferred_date)}</TableCell>
                  <TableCell>{formatDate(req.created_at)}</TableCell>
                  <TableCell align="center">
                    <Chip label={req.status} color={STATUS_COLORS[req.status] || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleView(req)}>
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      {/* Detail dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Trip Request</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={3}>
              {/* Plan */}
              <Box>
                <Typography variant="overline" color="text.secondary">Itinerary</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{selected.plan?.title || 'Untitled plan'}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label={`${selected.plan?.days_count ?? 0} days`} variant="outlined" />
                  <Chip size="small" label={`${selected.plan?.items_count ?? 0} items`} variant="outlined" />
                  <Chip size="small" label={`Est. ${formatCurrency(selected.plan?.calculated_total)}`} variant="outlined" sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main }} />
                </Stack>
                {selected.plan?.slug && (
                  <Button
                    size="small"
                    startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                    href={`/p/${selected.plan.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    View itinerary
                  </Button>
                )}
              </Box>

              <Divider />

              {/* Requester */}
              <Box>
                <Typography variant="overline" color="text.secondary">Requester</Typography>
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    {selected.requester?.name || '—'}
                  </Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    <a href={`mailto:${selected.requester?.email}`} style={{ color: 'inherit' }}>{selected.requester?.email || '—'}</a>
                  </Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    {selected.requester?.phone || '—'}
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Request details */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Travelers</Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                    <Groups sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    {selected.num_travelers ?? '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Preferred date</Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                    <Event sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    {formatDate(selected.preferred_date)}
                  </Typography>
                </Grid>
                {selected.message && (
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary">Message</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
                      <Typography variant="body2">{selected.message}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              <Divider />

              {/* Status control */}
              <Box>
                <Typography variant="overline" color="text.secondary">Update status</Typography>
                <ToggleButtonGroup
                  value={selected.status}
                  exclusive
                  onChange={(e, val) => handleStatusChange(val)}
                  size="small"
                  disabled={updating}
                  sx={{ mt: 0.5, flexWrap: 'wrap', '& .MuiToggleButton-root': { borderRadius: 9999, px: 2, textTransform: 'capitalize', mr: 1, mb: 1, border: `1px solid ${theme.palette.grey[300]}` } }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <ToggleButton key={s} value={s}>{s}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: 9999 }}>Close</Button>
          {selected && selected.status !== 'closed' && (
            <Button
              variant="contained"
              startIcon={<BookIcon />}
              onClick={openBook}
              sx={{ borderRadius: 9999 }}
            >
              {selected.status === 'confirmed' ? 'Book' : 'Approve & Book'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve & Book dialog */}
      <Dialog open={bookOpen} onClose={() => !booking && setBookOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selected?.status === 'confirmed' ? 'Book this trip' : 'Approve & book'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Creates the booking for {selected?.requester?.name || 'the customer'} and emails them. Set the final figures below.
          </Typography>
          <Stack spacing={2.5}>
            <TextField
              label="Travel date"
              type="date"
              value={bookForm.travel_date}
              onChange={(e) => setBookForm((f) => ({ ...f, travel_date: e.target.value }))}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: tomorrow() }}
            />
            <TextField
              label="Travelers"
              type="number"
              value={bookForm.num_travelers}
              onChange={(e) => setBookForm((f) => ({ ...f, num_travelers: e.target.value }))}
              fullWidth
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Total price"
              type="number"
              value={bookForm.total_price}
              onChange={(e) => setBookForm((f) => ({ ...f, total_price: e.target.value }))}
              fullWidth
              helperText="Final price for the whole trip (split across travelers on the booking)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBookOpen(false)} disabled={booking} sx={{ borderRadius: 9999 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBook}
            disabled={booking}
            startIcon={booking ? <CircularProgress size={16} /> : <BookIcon />}
            sx={{ borderRadius: 9999 }}
          >
            {booking ? 'Booking...' : 'Confirm booking'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanRequestsManagement;
